import { ReactUtils } from '@festina-life/core'
import cx from 'classnames'
import type { ReactNode } from 'react'

import { IconButton } from '../../actions/iconButton/IconButton'
import {
  type PropsWithSafeExtra,
  safeRestProps,
} from '../../libs/safeRestProps'
import { Icon } from '../../structure/icon/Icon'
import { Typography } from '../../structure/typography/Typography'
import { CircularSpinner } from '../circularSpinner/CircularSpinner'
import { Tooltip } from '../tooltip/Tooltip'
import {
  severityColorForegroundStyles,
  severityColorStyles,
} from './severityColorStyles'
import type { Severity } from './types'

export type AlertProps = PropsWithSafeExtra<
  'div',
  'className' | 'children',
  {
    /**
     * See explanation of each severity in the `Severity` type
     */
    severity?: Severity
    title?: string
    isLoading?: boolean
  } & (
    | {
        variant?: 'standard'
        onClose?: () => void
        action?: ReactNode
      }
    | {
        variant?: 'mini'
        onClose?: never
        action?: never
      }
  )
>

/**
 * Component for notifying the user of an important issue. Use sparingly only one or very few places should display alerts at any one time.
 */
export const Alert = ({
  title,
  severity = 'error',
  variant = 'standard',
  children,
  onClose,
  action = null,
  className,
  isLoading = false,
  extraProps,
  ...restProps // Needed since we can pass other props like aria-*, data-* etc. or spread that isn't type checked
}: AlertProps) => {
  const alertContent =
    title || children ? (
      <>
        {title && (
          <Typography weight="semibold" className="text-inherit!">
            {title}
          </Typography>
        )}
        {children}
      </>
    ) : null

  return {
    standard: (
      <div
        {...safeRestProps(restProps, extraProps)}
        role="alert"
        className={cx(
          'rounded-lg p-2 flex gap-2 items-start',
          'max-w-inputs-and-alerts', // so only if alerts are used in a context that has fixed width inputs then alerts respects that max width. Should we decide to make all inputs fixed width we'd need to revisit "link".
          severityColorStyles(isLoading ? 'info' : severity),
          className,
        )}
      >
        {isLoading ? (
          <CircularSpinner className="size-4 text-inherit!" />
        ) : (
          <Icon name={`status.${severity}`} color="unset" />
        )}
        <div
          className={cx(
            'grow', // To ensure actions and/or close button are pushed to the right if content is short
            'max-h-46 overflow-auto scroll-fade', // To ensure that if a lot of content is put into the alert it doesn't grow indefinitely and break the layout.
            'wrap-break-word', // Since errors from backendcode can have long words
          )}
        >
          {alertContent}
        </div>
        {action && <div className="-m-1">{action}</div>}
        {onClose && (
          <IconButton
            onClick={onClose}
            iconName="XMark"
            className="text-inherit! -m-1"
          />
        )}
      </div>
    ),
    mini: (
      <ReactUtils.ConditionalWrap
        condition={!!alertContent}
        wrap={(children) => (
          <Tooltip tip={alertContent} tipClassName="max-w-md">
            {children}
          </Tooltip>
        )}
      >
        {isLoading ? (
          <CircularSpinner className={cx('size-4', className)} />
        ) : (
          <Icon
            name={`status.${severity}`}
            color="unset"
            className={cx(
              severityColorForegroundStyles(severity),
              'opacity-100!', // To ensure they stand out more
              className,
            )}
          />
        )}
      </ReactUtils.ConditionalWrap>
    ),
  }[variant]
}
